// lane B (REQUEST-RECOVERY): the Inbox item of an interrupted request (`subject_kind:
// recovery`) carries its three actions, all through the one resolve route
// (`POST recoveries/{id}/resolve`): Continue (the DE tries again; held wakes go back to the
// queue), Take over (you hold it; the DE stops working on it) and Dismiss (the request is
// cancelled; a note says why). The server rechecks who may at the click.
import { Button, Input } from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HandIcon, PlayIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { invalidateDecided } from "../agents/agent-queries";
import { problemText } from "../de/de-status";

type Action = "continue" | "take_over" | "dismiss";

export function RecoveryActions({
  api,
  co,
  recoveryId,
  onResolved,
}: {
  api: Api;
  co: string;
  recoveryId: string;
  onResolved?: (action: Action) => void;
}) {
  const qc = useQueryClient();
  const [dismissing, setDismissing] = useState(false);
  const [note, setNote] = useState("");
  const go = useMutation({
    mutationFn: (a: { action: Action; note?: string }) =>
      api.client.recoveries.resolve({
        params: { co, id: recoveryId },
        body: a.note ? { action: a.action, note: a.note } : { action: a.action },
      }),
    onSuccess: (_r, a) => onResolved?.(a.action),
    onSettled: () => invalidateDecided(qc, co),
  });
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2" data-slot="recovery-actions">
      <Button
        size="xs"
        disabled={go.isPending}
        onClick={() => go.mutate({ action: "continue" })}
        data-slot="recovery-continue"
      >
        <PlayIcon aria-hidden="true" />
        Continue
      </Button>
      <Button
        size="xs"
        variant="outline"
        disabled={go.isPending}
        onClick={() => go.mutate({ action: "take_over" })}
        data-slot="recovery-take-over"
      >
        <HandIcon aria-hidden="true" />
        Take over
      </Button>
      {dismissing ? (
        <span className="inline-flex items-center gap-1">
          <Input
            aria-label="Why dismiss it"
            className="h-7 w-48 text-xs"
            placeholder="Why dismiss it?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            size="xs"
            variant="outline"
            disabled={go.isPending || note.trim().length === 0}
            onClick={() => go.mutate({ action: "dismiss", note: note.trim() })}
            data-slot="recovery-dismiss-confirm"
          >
            Dismiss
          </Button>
        </span>
      ) : (
        <Button
          size="xs"
          variant="ghost"
          disabled={go.isPending}
          onClick={() => setDismissing(true)}
          data-slot="recovery-dismiss"
        >
          <XIcon aria-hidden="true" />
          Dismiss
        </Button>
      )}
      {go.isError ? (
        <span role="alert" className="text-xs text-destructive">
          {problemText(go.error, "That did not go through.")}
        </span>
      ) : null}
    </span>
  );
}
